import { format } from 'd3-format';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';
import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { unitOfMeasure } from 'src/util/i18n/energyPrice';
import { i18nDecimalFormat } from 'src/util/i18n/handler';

/**
 * Renders daily trade summary totals as table footer rows within TradeListTable.
 * Aggregates and displays total volume, value, and average energy price for trades in a specific
 * direction.
 * @param {object} props - Component props
 * @param {Array} props.trades - Array of trade objects to aggregate
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.direction - Trade direction to filter
 * and summarize
 * @returns {React.ReactElement} - Table row with daily summary data
 */
function TradesSummary({ trades, direction }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const tradeDirection = direction === TRADE_DIRECTION_BUY
    ? 'buy'
    : 'sell';

  const dailyTotalBuy = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.trades_summary.foot.total_buy.label', defaultMessage: 'Daily Total Buy' });
  const dailyTotalSell = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.trades_summary.foot.total_sell_label', defaultMessage: 'Daily Total Sell' });

  const headerMessage = direction === TRADE_DIRECTION_BUY
    ? dailyTotalBuy
    : dailyTotalSell;

  const summaryVolumeUnitTitle = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.trades_summary.foot.trade_volume.abbr.title', defaultMessage: 'watt hour' });
  const summaryVolumeUnitLabel = intl.formatMessage({ id: 'trade.trade_list.trade_list_table.trades_summary.foot.label.trade_volume.abbr.label', defaultMessage: 'Wh' });

  const tradeVolume = (volume) => (
    <FormattedMessage
      id="trade.trade_list.trade_list_table.trades_summary.foot.trade_volume.text"
      defaultMessage="{summaryVolume}{summaryVolumeUnit}"
      values={{
        // TO DO: Implement d3-format locale support (PT-1124)
        summaryVolume: i18nDecimalFormat(format('.4s')(volume || 0), intl.locale),
        summaryVolumeUnit: (
          <abbr title={summaryVolumeUnitTitle}>{summaryVolumeUnitLabel}</abbr>
        ),
      }}
    />
  );

  const energyPrice = (value, volume) => {
    const avgEnergyPriceValue = intlCurrency.formatMessage(
      {
        id: 'trade.trade_list.trade_list_table.trades_summary.foot.avg_energy_price.label',
        defaultMessage: '{n, number, :: .00#}',
      },
      {
        n: volume > 0
          ? convertEnergyPricePowertracerToPartner(intlCurrency, value / volume)?.toNumber() || 0
          : 0,
      },
    );
    const energyPriceUnit = unitOfMeasure(intlCurrency);

    return (
      intl.formatMessage(
        {
          id: 'trade.trade_list.trade_list_table.trades_summary.foot.energy_price.text',
          defaultMessage: '{energyPrice} {energyPriceUnit}',
        },
        {
          energyPrice: <strong>{avgEnergyPriceValue}</strong>,
          energyPriceUnit,
        },
      )
    );
  };

  return (
    <tr>
      <th scope="row" colSpan={2}>{headerMessage}</th>
      <td colSpan={6}>
        {trades && trades.reduce((acc, trade) => {
          if (trade.direction !== direction) {
            return acc;
          }
          acc[0].value += trade.value;
          acc[0].volume += trade.volume;
          return acc;
        }, [{ value: 0, volume: 0 }]).map(({ value, volume }) => (
          <div data-testid={`total-${tradeDirection}`} key={`total-${tradeDirection}`}>
            <strong>{tradeVolume(volume)}</strong>
            <br />
            <strong>
              {intlCurrency.formatMessage(
                {
                  id: 'common.currency.value',
                  defaultMessage: '{n, number, :: .00 currency/AUD}',
                },
                { n: (value || 0) },
              )}
            </strong>
            {!!volume && volume > 0 && (
              <>
                <br />
                {energyPrice(value, volume)}
              </>
            )}
          </div>
        ))}
      </td>
    </tr>
  );
}

TradesSummary.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  trades: PropTypes.arrayOf(PropTypes.object).isRequired,
  direction: PropTypes.string.isRequired,
};

export default TradesSummary;
